import { ComplianceObjectType as ComplianceObjectTypeEnum } from "~/graphql/supergraphEnums";
import { ProfileNameFields } from "../../utils/types";
import { ChildProfile } from "./BankAccountCard/CreateBankAccountAction/types";
import {
  SmallProfileRelationshipType,
  SmallProfileType,
  ProfileType,
  AddressesType,
  PaymentAccount,
} from "./types";

export const getFullLegalName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  middleName?: string | null,
) => {
  if (!firstName || !lastName) {
    return "--"; // This case should never be hit since these are required fields, but good to have as a fallback
  }
  return `${firstName} ${middleName ? middleName + " " : ""}${lastName ?? ""}`;
};

export const getInvestingProfileName = (
  investingProfile?: Omit<ProfileNameFields, "id"> | null,
) =>
  investingProfile?.complianceObjectType === ComplianceObjectTypeEnum.Individual
    ? getFullLegalName(
        investingProfile?.firstName,
        investingProfile?.lastName,
        investingProfile?.middleName,
      )
    : (investingProfile?.legalName ?? "--");

export const getProfileName = (profile: ProfileType | ChildProfile) =>
  profile?.complianceObjectType === ComplianceObjectTypeEnum.Individual
    ? getFullLegalName(profile?.firstName, profile?.lastName, profile?.middleName)
    : (profile?.legalName ?? "--");

export const getProfileNameForProfileType = (profile: SmallProfileType | null) =>
  profile?.complianceObjectType === ComplianceObjectTypeEnum.Individual
    ? getFullLegalName(profile?.firstName, profile?.lastName, profile?.middleName)
    : (profile?.legalName ?? "--");

export const getRelationships = (
  relationships: Pick<SmallProfileRelationshipType, "relationshipType">[] = [],
): string => {
  if (relationships.length === 0) return "--";

  const relationshipTypes = new Set(relationships.map((r) => r.relationshipType));

  const labels: string[] = [];
  if (relationshipTypes.has("owner")) labels.push("Owner");
  if (relationshipTypes.has("controller")) labels.push("Controller");
  if (relationshipTypes.has("fatca_substantial_owner")) labels.push("FATCA Substantial owner");
  if (relationshipTypes.has("crs_controlling_person")) labels.push("CRS Controlling person");

  return labels.length > 0 && !relationshipTypes.has("unassigned") ? labels.join(", ") : "--";
};

export const getIsPaymentBeneficiary = (
  profileId: number | undefined,
  paymentAccounts?: PaymentAccount[],
) => {
  const paymentProfileIds = paymentAccounts?.map((account) => account?.profileId);
  return paymentProfileIds?.includes(profileId);
};

export const getOwnershipPercentageStr = (
  relationships: SmallProfileRelationshipType[] | undefined,
) => {
  const ownerRelationships = relationships?.filter((p) => p.percentOwnership) || [];
  return ownerRelationships.length ? `${ownerRelationships[0].percentOwnership}%` : "--";
};

export const getOwnershipPercentage = (
  relationships: SmallProfileRelationshipType[] | undefined,
) => {
  const ownerRelationships = relationships?.filter((p) => p.percentOwnership) || [];
  return ownerRelationships.length ? ownerRelationships[0].percentOwnership : null;
};

export const getTitle = (relationships: SmallProfileRelationshipType[] | undefined) => {
  const titleRelationships = relationships?.filter((p) => p.title) || [];
  return titleRelationships.length ? titleRelationships[0].title : null;
};

export const formatOwnershipDisplay = (value: string | null | undefined): string => {
  if (value == null || value === "" || value === "--") return "--";
  return value;
};

export const getCountryName = (addresses: AddressesType[] | undefined) => {
  if (!addresses?.length) {
    return "None";
  }

  // Check for an address with isPrimaryDomicile set to true
  // Note: isPrimaryDomicile might not be available in all queries
  const primaryDomicileAddress = addresses.find(
    (address) => address.isPrimaryDomicile === true,
  );
  if (primaryDomicileAddress?.country?.name) {
    return primaryDomicileAddress.country.name;
  }

  // Fall back to original behavior if no primary domicile is found
  const uniqueAddresses = addresses.filter(
    (value, index, array) => array.indexOf(value) === index,
  );
  if (uniqueAddresses.length === 1) {
    return addresses[0].country?.name || "";
  } else {
    return "Multiple";
  }
};
